import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type OrgLogRecord, type LogSeverity, type OrganizationSummary } from '../lib/api';
import { Card } from '../components/Card';
import { PageSpinner } from '../components/Spinner';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { Radar, RefreshCw, Building2, ShieldAlert } from 'lucide-react';

const PLATFORM_ORG = '_platform';

// Severity presentation — house rule: dot + label colour, NO stripes / glows.
const SEVERITY: Record<LogSeverity, { label: string; dot: string; text: string }> = {
  info: { label: 'Info', dot: 'bg-gray-500', text: 'text-gray-400' },
  warn: { label: 'Warn', dot: 'bg-amber-500', text: 'text-amber-400' },
  error: { label: 'Error', dot: 'bg-red-500', text: 'text-red-400' },
};

const CATEGORY_LABEL: Record<string, string> = {
  procedure_violation: 'Procedure violation',
  auth_denied: 'Permission denied',
  system_error: 'System error',
};

function LogRow({ record }: { record: OrgLogRecord }) {
  const sev = SEVERITY[record.severity] ?? SEVERITY.info;
  const details = record.details ?? {};
  const detailStr = Object.entries(details)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
    .join(' · ');
  const catLabel = record.category ? CATEGORY_LABEL[record.category] ?? record.category : null;

  return (
    <div className="flex items-start gap-3 rounded-lg bg-white/[0.03] px-3 py-2.5">
      <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', sev.dot)} title={sev.label} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className={cn('font-mono font-semibold', sev.text)}>{record.action}</span>
          {catLabel && (
            <span className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-300 bg-amber-500/10">
              {catLabel}
            </span>
          )}
          <span className="rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-500 bg-white/[0.04]">
            {record.source}
          </span>
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs">
          {record.actorId && record.actorId !== 'system' ? (
            <Link
              to={`/admin/user-lookup?id=${encodeURIComponent(record.actorId)}`}
              className="font-mono text-gray-400 hover:text-ink-primary transition-colors"
            >
              {record.actorId}
            </Link>
          ) : (
            <span className="font-mono text-gray-600">system</span>
          )}
          {record.target && (
            <>
              <span className="text-gray-600">→</span>
              <span className="font-mono text-gray-400 truncate">{record.target}</span>
            </>
          )}
        </div>
        {detailStr && <p className="mt-0.5 text-[10px] text-gray-600 truncate">{detailStr}</p>}
      </div>
      <span className="shrink-0 text-[10px] text-gray-600" title={new Date(record.createdAt).toISOString()}>
        {new Date(record.createdAt).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}
      </span>
    </div>
  );
}

export function Observability() {
  const [orgId, setOrgId] = useState<string>('');
  const [severity, setSeverity] = useState<'all' | LogSeverity>('all');
  const [live, setLive] = useState(true);

  const { data: orgs } = useQuery({
    queryKey: ['orgs'],
    queryFn: () => api.orgs(),
  });

  // Build the org selector: every connected org + the Platform/unscoped lane.
  const orgOptions = useMemo(() => {
    const list: Array<{ value: string; label: string }> = (orgs ?? []).map((o: OrganizationSummary) => ({
      value: o.id,
      label: o.name || o.slug || o.id,
    }));
    return [...list, { value: PLATFORM_ORG, label: 'Platform / unscoped' }];
  }, [orgs]);

  // Default to the first real org once orgs load (falls back to platform lane).
  const activeOrg = orgId || orgOptions[0]?.value || PLATFORM_ORG;
  const activeOrgLabel = orgOptions.find((o) => o.value === activeOrg)?.label ?? activeOrg;

  const { data: logs, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['admin', 'org-logs', activeOrg, severity],
    queryFn: () => api.admin.orgLogs(activeOrg, { severity: severity === 'all' ? undefined : severity, limit: 100 }),
    enabled: !!activeOrg,
    refetchInterval: live ? 8000 : false,
  });

  const records = logs ?? [];

  return (
    <div>
      <div className={cn('mb-8 flex items-center gap-3', SETTLE)} style={settleDelay(0)}>
        <Radar className="h-7 w-7 text-accent-500" />
        <div>
          <h1 className="text-2xl font-bold text-white">Observability</h1>
          <p className="text-xs text-gray-500">
            Org-isolated audit trail — every action, faithfully recorded. Each org's logs live in
            their own partition, so they can never appear under another org.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className={SETTLE} style={settleDelay(1)}>
        <Card className="mb-6">
          <div className="flex flex-wrap items-center gap-3">
            {/* Org selector */}
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-gray-500" />
              <select
                value={activeOrg}
                onChange={(e) => setOrgId(e.target.value)}
                className="input font-medium"
              >
                {orgOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Severity filter */}
            <div className="flex items-center gap-1">
              <ShieldAlert className="h-3.5 w-3.5 text-gray-500" />
              {(['all', 'info', 'warn', 'error'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setSeverity(s)}
                  className={cn(
                    'rounded-md px-2 py-1 text-xs transition-colors',
                    severity === s ? 'bg-white/[0.07] text-accent-500' : 'text-gray-500 hover:text-gray-300',
                  )}
                >
                  {s === 'all' ? 'all' : SEVERITY[s].label.toLowerCase()}
                </button>
              ))}
            </div>

            <div className="ml-auto flex items-center gap-2">
              {/* Live toggle */}
              <button
                onClick={() => setLive((v) => !v)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs transition-colors',
                  live ? 'text-green-400 bg-green-500/10' : 'text-gray-500 hover:text-gray-300',
                )}
                title={live ? 'Live tail on (refreshing every 8s)' : 'Live tail off'}
              >
                <span className={cn('h-2 w-2 rounded-full', live ? 'bg-green-500 animate-pulse' : 'bg-gray-600')} />
                {live ? 'Live' : 'Paused'}
              </button>
              {/* Manual refresh */}
              <button onClick={() => refetch()} className={cn('btn-ghost px-2', MORPH)} title="Refresh now">
                <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
              </button>
            </div>
          </div>
          <p className="mt-2 text-xs text-ink-secondary">
            Showing logs for: <span className="font-semibold text-ink-primary">{activeOrgLabel}</span>
          </p>
        </Card>
      </div>

      {/* Results */}
      {isLoading ? (
        <PageSpinner />
      ) : records.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-500">
            No logs for this org yet. Actions appear here once the audit log starts capturing data
            for <span className="font-semibold">{activeOrgLabel}</span>.
          </p>
        </Card>
      ) : (
        <div className={cn('space-y-1.5', SETTLE)} style={settleDelay(2)}>
          {records.map((r) => (
            <LogRow key={r.sk} record={r} />
          ))}
          {records.length >= 100 && (
            <p className="mt-4 text-center text-xs text-gray-600">Showing latest 100 records</p>
          )}
        </div>
      )}
    </div>
  );
}
